import React, { useState } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import { styled } from '@mui/material/styles';
import Grid from '@mui/material/Grid';
import { useDispatch } from 'react-redux';
import { setMessage } from '../toasts/toastSlice';
import { setConfirmationAnalytics } from './orderSlice';
import { useTranslation } from 'react-i18next';
import { NOTIFICATION_TYPES } from '../../config/consts/commons';
import ModalBarcodeScanner from './ModalBarcodeScanner';
import getOrderRecipientDisplayName from './utils/getOrderRecipientDisplayName';

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialogContent-root': {
    padding: theme.spacing(2),
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(1),
  },
}));

const DELIVERY_CONFIRMATION_TYPE_DEFAULT = 'scanQR';

function UnifiedDialog({
  open,
  type,
  onClose,
  onConfirmManual,
  onScanQR,
  onScanBarcode,
  onConfirm,
  textFieldValue,
  handleTextFieldChange,
  order,
}) {
  const { t } = useTranslation(['title', 'description', 'glossary', 'button']);

  return (
    <BootstrapDialog onClose={onClose} open={open}>
      <DialogTitle>
        {type === 'scanQR'
          ? t('orders.deliver.scan_confirmation', { ns: 'title' })
          : t('orders.deliver.confirmation', { ns: 'title' })}
        <Typography sx={{ fontWeight: 'bold' }}># {order}</Typography>
      </DialogTitle>

      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent>
        <Typography gutterBottom>
          {type === 'scanQR'
            ? t('orders.deliver.scan_confirmation', { ns: 'description' })
            : t('orders.deliver.confirmation', { ns: 'description' })}
        </Typography>
        {type !== 'scanQR' && (
          <TextField
            id="outlined-basic"
            label={t('code', { ns: 'glossary' })}
            multiline
            fullWidth
            maxRows={4}
            value={textFieldValue}
            onChange={handleTextFieldChange}
            onPaste={(e) => {
              e.preventDefault();
            }}
            variant="outlined"
          />
        )}
      </DialogContent>
      <DialogActions sx={{ display: 'flex', flexDirection: 'column' }}>
        <Grid container>
          {type === 'scanQR' ? (
            <>
              <Grid item xs={12} md={12} rowSpacing={2}>
                <Button
                  fullWidth
                  variant="contained"
                  disableElevation
                  onClick={onScanQR}
                  sx={{
                    mb: 1,
                    backgroundColor: '#5F36C4',
                    color: '#FFFFFF',
                    '&:hover': {
                      backgroundColor: '#5F36C4',
                    },
                    '&:active': {
                      backgroundColor: '#5F36C4',
                    },
                  }}
                >
                  {t('scan_qr', { ns: 'button' })}
                </Button>
              </Grid>

              <Grid item xs={12} md={12} rowSpacing={2}>
                <Button
                  fullWidth
                  variant="contained"
                  disableElevation
                  sx={{
                    mb: 1,
                    backgroundColor: '#E0E0E0',
                    color: 'black',
                    '&:hover': {
                      backgroundColor: '#E0E0E0',
                    },
                    '&:active': {
                      backgroundColor: '#E0E0E0',
                    },
                  }}
                  onClick={onConfirmManual}
                >
                  {t('manually_confirm', { ns: 'button' })}
                </Button>
              </Grid>
            </>
          ) : (
            <>
              <Grid item xs={12} md={12} rowSpacing={2}>
                <Button
                  fullWidth
                  variant="contained"
                  disableElevation
                  disabled={textFieldValue.trim() === ''}
                  onClick={onConfirm}
                  sx={{
                    mb: 1,
                    backgroundColor: '#5F36C4',
                    color: '#FFFFFF',
                    '&:hover': {
                      backgroundColor: '#5F36C4',
                    },
                    '&:active': {
                      backgroundColor: '#5F36C4',
                    },
                  }}
                >
                  {t('confirm', { ns: 'button' })}
                </Button>
              </Grid>
              <Grid item xs={12} md={12} rowSpacing={2}>
                <Button
                  fullWidth
                  variant="contained"
                  disableElevation
                  sx={{
                    backgroundColor: '#E0E0E0',
                    color: 'black',
                    '&:hover': {
                      backgroundColor: '#E0E0E0',
                    },
                    '&:active': {
                      backgroundColor: '#E0E0E0',
                    },
                  }}
                  onClick={onClose}
                >
                  {t('return', { ns: 'button' })}
                </Button>
              </Grid>
            </>
          )}
        </Grid>
      </DialogActions>
    </BootstrapDialog>
  );
}

export default function CustomDialogsContainer({
  open,
  onClose,
  onConfirmDelivery,
  selectedOrder,
  handleQR,
}) {
  const { t } = useTranslation(['system']);
  const [dialogType, setDialogType] = useState(
    DELIVERY_CONFIRMATION_TYPE_DEFAULT
  );
  const [textFieldValue, setTextFieldValue] = useState('');
  const dispatch = useDispatch();
  const [barcodeOpen, setBarcodeOpen] = useState(false);

  const handleTextFieldChange = (e) => {
    setTextFieldValue(e.target.value);
  };

  const handleClose = () => {
    if (onClose) {
      onClose();
    }
    setDialogType(DELIVERY_CONFIRMATION_TYPE_DEFAULT);
  };

  const fullNameLowerCase = selectedOrder
    ? getOrderRecipientDisplayName(selectedOrder).toLowerCase()
    : '';
  const handleConfirm = () => {
    const textNameValueClean = selectedOrder?.order_code?.trim() || '';

    const textFieldValueClean = textFieldValue.trim();

    if (textNameValueClean === textFieldValueClean) {
      dispatch(
        setMessage({
          body: t('SUCCESS_ORDER_CONFIRM', {
            ns: 'system',
            order: selectedOrder?.order_code,
          }),
          type: NOTIFICATION_TYPES.SUCCESS,
          autoHideDuration: 8000,
        })
      );
      onConfirmDelivery();
    } else {
      dispatch(
        setMessage({
          body: t('ERROR_CODE_CONFIRM_ORDER', { ns: 'system' }),
          type: 'error',
          autoHideDuration: 8000,
        })
      );
    }

    if (onClose) {
      onClose();
      setDialogType(DELIVERY_CONFIRMATION_TYPE_DEFAULT);
    }
    setTextFieldValue('');
  };
  const handleConfirmManual = () => {
    dispatch(setConfirmationAnalytics('manual-confirmation'));
    setDialogType('confirmManual');
  };

  const handleOpenBarcode = () => {
    setBarcodeOpen(true);
  };

  const handleCloseBarcode = () => {
    setBarcodeOpen(false);
  };

  return (
    <>
      <UnifiedDialog
        open={open}
        type={dialogType}
        onClose={handleClose}
        onConfirmManual={handleConfirmManual}
        onScanQR={handleQR}
        onScanBarcode={handleOpenBarcode}
        onConfirm={handleConfirm}
        textFieldValue={textFieldValue}
        order={fullNameLowerCase}
        handleTextFieldChange={handleTextFieldChange}
      />
      <ModalBarcodeScanner open={barcodeOpen} onClose={handleCloseBarcode} />
    </>
  );
}
